import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  composeQueryParams,
  initialDashboardPageMetadata,
  mergeDshbPageMetadataObjects,
  paramsSerializer,
  apiError
} from "../../utils";

export const readAllLinksForDashboardAsync = createAsyncThunk(
  "link/readAll",
  async (payload = {}, { getState }) => {
    const { metadata } = getState().link;
    const params = composeQueryParams(payload, metadata);
    const response = await axios.get("/api/admin/links", { params, paramsSerializer });
    return { links: response.data };
  }
);

export const upsertLinkAsync = createAsyncThunk(
  "link/upsert",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/link`, payload.link);
      return { link: response.data };
    } catch (error) {
      return rejectWithValue(apiError(error));
    }
  }
);

export const deleteLinkAsync = createAsyncThunk("/link/delete", async payload => {
  await axios.delete(`/api/link/${payload.link.id}`);
  return { id: payload.link.id };
});

export const linkSlice = createSlice({
  name: "link",
  initialState: {
    loadFailed: false,
    failedLoadArgs: {},
    loading: true,
    error: null,
    links: [],
    metadata: initialDashboardPageMetadata
  },
  reducers: {
    clearError: (state, action) => {
      if (state.error) state.error[action.payload.field] = null;
    },
    clearErrors: state => {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(readAllLinksForDashboardAsync.pending, state => {
        state.loading = true;
        state.loadFailed = false;
      })
      .addCase(readAllLinksForDashboardAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { items, ...metadata } = action.payload.links || {};
        state.links = items || [];
        state.metadata = mergeDshbPageMetadataObjects(state.metadata, metadata);
      })
      .addCase(readAllLinksForDashboardAsync.rejected, (state, action) => {
        state.loading = false;
        if (action.meta.aborted) return;
        state.loadFailed = true;
        state.failedLoadArgs = action.meta.arg || {};
        state.links = [];
      })
      .addCase(upsertLinkAsync.pending, state => {
        state.loading = true;
      })
      .addCase(upsertLinkAsync.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.link[1]) {
          if (action.payload.link[0]) state.links.unshift(action.payload.link[0]);
        } else {
          state.links = (state.links || []).map(link =>
            link.id === action.payload.link[0].id ? action.payload.link[0] : link
          );
        }
      })
      .addCase(upsertLinkAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.fields ?? null;
      })
      .addCase(deleteLinkAsync.pending, state => {
        state.loading = true;
      })
      .addCase(deleteLinkAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.links = state.links.filter(link => link.id !== action.payload.id);
      })
      .addCase(deleteLinkAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      });
  }
});

export const { clearError, clearErrors } = linkSlice.actions;
export default linkSlice.reducer;
